/**
 * MultiBuy Builder - api.js
 * Thin wrapper around Shopify's public storefront AJAX endpoints.
 * Only uses documented, unauthenticated Shopify endpoints:
 *   GET  /collections/{handle}/products.json
 *   GET  /products/{handle}.js
 *   GET  /cart.js
 *   POST /cart/add.js
 *   POST /cart/change.js
 *   POST /cart/update.js
 * Attaches to window.MB.api
 */
(function (window) {
  'use strict';

  var MB = (window.MB = window.MB || {});
  var routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function url(path) {
    if (routes === '/') return path;
    return routes.replace(/\/$/, '') + path;
  }

  function request(path, options) {
    options = options || {};
    var opts = {
      method: options.method || 'GET',
      headers: Object.assign(
        { 'Content-Type': 'application/json', Accept: 'application/json' },
        options.headers || {}
      ),
      credentials: 'same-origin',
      signal: options.signal
    };
    if (options.body) opts.body = JSON.stringify(options.body);

    return fetch(url(path), opts).then(function (res) {
      if (!res.ok) {
        return res
          .json()
          .catch(function () {
            return { message: res.statusText };
          })
          .then(function (err) {
            var e = new Error(err.description || err.message || 'MultiBuy request failed');
            e.status = res.status;
            e.data = err;
            throw e;
          });
      }
      return res.json();
    });
  }

  var collectionCache = {};
  var productCache = {};

  /**
   * Fetch every product in a collection (paginated 250 at a time).
   * Returns a normalized array with only the fields the app needs.
   * Page 1 goes first (there's no way to know the page count up front);
   * if it comes back full (250, meaning more pages likely exist), the
   * rest up to maxPages are fetched in parallel instead of one at a
   * time - total wait becomes roughly the slowest single page instead
   * of the sum of all of them, which is where large collections' load
   * time was mostly going. Any page past the real end just resolves
   * empty and is dropped by normalizeProduct's map (a no-op on []).
   */
  function getCollectionProducts(handle, opts) {
    opts = opts || {};
    var maxPages = opts.maxPages || 4; // 4 x 250 = 1000 product safety cap
    var cacheKey = handle + ':' + maxPages;
    if (collectionCache[cacheKey] && !opts.fresh) {
      return Promise.resolve(collectionCache[cacheKey]);
    }

    function fetchPage(page) {
      return request('/collections/' + handle + '/products.json?limit=250&page=' + page).then(
        function (data) {
          return (data && data.products) || [];
        }
      );
    }

    return fetchPage(1).then(function (firstPage) {
      if (firstPage.length < 250 || maxPages <= 1) {
        var all = firstPage.map(normalizeProduct);
        collectionCache[cacheKey] = all;
        return all;
      }
      var rest = [];
      for (var p = 2; p <= maxPages; p++) rest.push(fetchPage(p));
      return Promise.all(rest).then(function (pages) {
        var all = firstPage.concat.apply(firstPage, pages).map(normalizeProduct);
        collectionCache[cacheKey] = all;
        return all;
      });
    });
  }

  var allProductsPromise = null;
  function getAllProducts() {
    if (allProductsPromise) return allProductsPromise;
    function page(number, products) {
      return request('/products.json?limit=250&page=' + number).then(function (data) {
        var batch = (data && data.products) || [];
        var combined = products.concat(batch);
        return batch.length === 250 ? page(number + 1, combined) : combined.map(normalizeProduct);
      });
    }
    allProductsPromise = page(1, []).catch(function (error) {
      allProductsPromise = null;
      throw error;
    });
    return allProductsPromise;
  }

  function getProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return request('/products/' + handle + '.js').then(function (data) {
      var normalized = normalizeProduct(data);
      productCache[handle] = normalized;
      return normalized;
    });
  }

  function normalizeProduct(p) {
    var variants = (p.variants || []).map(function (v) {
      return {
        id: v.id,
        title: v.title,
        available: v.available !== false,
        price: toCents(v.price),
        compare_at_price: toCents(v.compare_at_price),
        sku: v.sku,
        options: v.options || (v.option1 ? [v.option1, v.option2, v.option3].filter(Boolean) : []),
        featured_image: v.featured_image
          ? typeof v.featured_image === 'string'
            ? v.featured_image
            : v.featured_image.src
          : null
      };
    });

    var minVariant = variants.reduce(function (min, v) {
      return !min || v.price < min.price ? v : min;
    }, null);

    return {
      id: p.id,
      handle: p.handle,
      title: p.title,
      vendor: p.vendor,
      product_type: p.product_type || p.type,
      tags: p.tags || [],
      available: p.available !== false,
      images: (p.images || []).map(function (img) {
        return typeof img === 'string' ? img : img.src;
      }),
      featured_image: p.featured_image
        ? typeof p.featured_image === 'string'
          ? p.featured_image
          : p.featured_image.src
        : (p.images && p.images[0] && (typeof p.images[0] === 'string' ? p.images[0] : p.images[0].src)) || null,
      options: (p.options || []).map(function (o) {
        return typeof o === 'string' ? { name: o } : { name: o.name, values: o.values };
      }),
      // Only present from /products/{handle}.js (Quick View's getProduct) -
      // the lighter /collections/{handle}/products.json listing payload
      // doesn't include it, so this is '' there.
      description: p.description || p.body_html || '',
      variants: variants,
      price: minVariant ? minVariant.price : toCents(p.price),
      compare_at_price: minVariant ? minVariant.compare_at_price : toCents(p.compare_at_price),
      created_at: p.created_at,
      url: p.url || '/products/' + p.handle
    };
  }

  function toCents(price) {
    if (price === null || price === undefined) return 0;
    if (typeof price === 'number') {
      // Storefront .js endpoint already returns cents for variant price;
      // products.json returns decimal strings. Detect decimals.
      return price;
    }
    var num = parseFloat(price);
    if (isNaN(num)) return 0;
    // products.json variant prices are decimal strings like "49.99"
    return Math.round(num * 100);
  }

  function getCart() {
    return request('/cart.js');
  }

  /**
   * items: [{ id: variantId, quantity: number, properties: {} }]
   */
  function addToCart(items) {
    return request('/cart/add.js', { method: 'POST', body: { items: items } });
  }

  function changeCartLine(line, quantity) {
    return request('/cart/change.js', { method: 'POST', body: { line: line, quantity: quantity } });
  }

  function updateCartNote(note) {
    return request('/cart/update.js', { method: 'POST', body: { note: note } });
  }

  MB.api = {
    getCollectionProducts: getCollectionProducts,
    getAllProducts: getAllProducts,
    getProduct: getProduct,
    getCart: getCart,
    addToCart: addToCart,
    changeCartLine: changeCartLine,
    updateCartNote: updateCartNote,
    normalizeProduct: normalizeProduct
  };
})(window);
